// Types - Read models
import type {
  ChildDetailsReadModel,
  ParentDashboardReadModel
} from '@c1/dashboard.types.read-models';
import { navigateTo } from 'nuxt/app';

/**
 * This middleware function is used to load the necessary data for the parent dashboard
 * and child details pages. It ensures that the required data is fetched from the API
 * and loaded into the store when a route change occurs. The function performs the following tasks:
 *
 * 1. Initializes the `parentDashboardStore` to manage parent dashboard data.
 * 2. Uses `from.params` to extract the `userId` from the route parameters.
 * 3. Sets flags to determine whether data needs to be fetched based on whether the store is already loaded:
 *    - `bfetchChildrenFromServer`: Determines if the list of children associated with the parent account needs to be fetched.
 *    - `bfetchChildDataFromServer`: Determines if the specific child data for the provided `userId` needs to be fetched.
 * 4. Fetches the parent dashboard data from the `/api/parent-dashboard` endpoint only if the parent dashboard store is not loaded.
 * 5. Fetches child details from the `/api/child-details` endpoint for the corresponding `userId` only if it's not already loaded in the store.
 * 6. Awaits the completion of both API requests using `Promise.all` to handle them concurrently.
 * 7. Loads the fetched data into the `parentDashboardStore`:
 *    - If the parent dashboard data is fetched, it loads the children associated with the parent into the store.
 *    - If child details data is fetched, it loads the specific child data associated with the `userId` into the store.
 *
 * This middleware ensures the store is updated with the latest data when navigating to routes
 * within the parent dashboard or child details views, handling any errors by displaying an error page if the API fails.
 */

export default defineNuxtRouteMiddleware(async (from) => {
  /**
   * LOAD STORES - Parent dashboard
   */
  const parentDashboardStore = useParentDashboardStore();

  // Get route params
  const params = { ...from.params };
  // Gets userId from params
  const userId = params.userId as string;

  // Boolean flags to check if the stores are loaded
  const bfetchChildrenFromServer = !parentDashboardStore.isLoaded;
  const bfetchChildDataFromServer = !parentDashboardStore[userId];

  /**
   *  Fetch list of children associated to the parent account from API and load the stores
   */
  const fetchParentDashboard = bfetchChildrenFromServer
    ? useFetchHelper().fetch<ParentDashboardReadModel>(
        '/api/parent-dashboard',
        {
          fatal: true,
          errorHandlingStrategy: 'showErrorPage' // Show error page if API fails
        }
      )
    : null;

  /**
   *  Fetch the data for view child details page for respective user
   */
  const fetchChildDetails = bfetchChildDataFromServer
    ? useFetchHelper().fetch<ChildDetailsReadModel>('/api/child-details', {
        params: { userId },
        fatal: true,
        errorHandlingStrategy: 'showErrorPage' // Show error page if API fails
      })
    : null;

  const [parentDashboardData, childData] = await Promise.all([
    fetchParentDashboard,
    fetchChildDetails
  ]);

  //Load the stores with fetched data
  if (parentDashboardData?.data?.value) {
    parentDashboardStore.load(parentDashboardData?.data.value.children);
  }
  if (parentDashboardStore.children[userId]) {
    if (childData?.data?.value) {
      parentDashboardStore.loadChildData(userId, childData?.data.value);
    }
  } else {
    return await navigateTo('/parent/dashboard');
  }
});
